import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "../components/layout.tsx";
import { useAuth } from "../context/auth.tsx";
import { getMarketingOverview, type MarketingOverview } from "../api/marketing.ts";

export function MarketingOverviewPage() {
  const { token } = useAuth();
  const [data, setData] = useState<MarketingOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (token == null) return;
    setLoading(true);
    getMarketingOverview(token)
      .then(setData)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }, [token]);

  const cards = [
    { label: "Discovered", count: data?.discovery.total ?? 0, to: "/marketing/discovery", color: "border-blue-500/30 bg-blue-900/10" },
    { label: "Qualified Leads", count: data?.discovery.qualified ?? 0, to: "/marketing/discovery", color: "border-green-500/30 bg-green-900/10" },
    { label: "Website Analyses", count: data?.discovery.websites ?? 0, to: "/marketing/research", color: "border-teal-500/30 bg-teal-900/10" },
    { label: "Content Pieces", count: data?.content.total ?? 0, to: "/marketing/content", color: "border-purple-500/30 bg-purple-900/10" },
    { label: "Published Content", count: data?.content.published ?? 0, to: "/marketing/content", color: "border-indigo-500/30 bg-indigo-900/10" },
    { label: "Social Posts", count: data?.social.total ?? 0, to: "/marketing/social", color: "border-pink-500/30 bg-pink-900/10" },
    { label: "Campaigns", count: data?.campaigns.total ?? 0, to: "/marketing/campaigns", color: "border-orange-500/30 bg-orange-900/10" },
    { label: "Outreach Drafts", count: data?.outreach.total ?? 0, to: "/marketing/outreach", color: "border-yellow-500/30 bg-yellow-900/10" },
    { label: "Awaiting Approval", count: data?.outreach.pendingApproval ?? 0, to: "/marketing/outreach", color: "border-red-500/30 bg-red-900/10" },
  ];

  return (
    <Layout>
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-white">Marketing Overview</h1>

        {loading && <p className="text-gray-400">Loading…</p>}
        {error != null && <p className="text-red-400">{error}</p>}

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {cards.map((card) => (
            <Link
              key={card.to + card.label}
              to={card.to}
              className={`block rounded-lg border p-5 transition-all hover:brightness-110 ${card.color}`}
            >
              <p className="text-3xl font-bold text-white">{loading ? "—" : card.count.toLocaleString()}</p>
              <p className="text-sm text-gray-400 mt-1">{card.label}</p>
            </Link>
          ))}
        </div>

        {data != null && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="rounded-lg border border-gray-800 p-5 space-y-2">
              <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">Content Pipeline</h2>
              <div className="space-y-1 text-sm text-gray-400">
                <div className="flex justify-between"><span>Draft</span><span className="text-white">{data.content.draft}</span></div>
                <div className="flex justify-between"><span>Approved</span><span className="text-white">{data.content.approved}</span></div>
                <div className="flex justify-between"><span>Published</span><span className="text-white">{data.content.published}</span></div>
              </div>
            </div>
            <div className="rounded-lg border border-gray-800 p-5 space-y-2">
              <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">Outreach Pipeline</h2>
              <div className="space-y-1 text-sm text-gray-400">
                <div className="flex justify-between"><span>Draft</span><span className="text-white">{data.outreach.draft}</span></div>
                <div className="flex justify-between"><span>Pending Approval</span><span className="text-red-400 font-medium">{data.outreach.pendingApproval}</span></div>
                <div className="flex justify-between"><span>Approved (ready to send)</span><span className="text-green-400 font-medium">{data.outreach.approved}</span></div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
